import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import { stripEmojis } from "./pdfTextUtils";
import { getTableColumnWidths } from "./exportTableLayout";
import type {
  Root,
  Content,
  PhrasingContent,
  Paragraph as MdParagraph,
  Heading as MdHeading,
  List as MdList,
  ListItem as MdListItem,
  Blockquote as MdBlockquote,
  Code as MdCode,
  ThematicBreak as MdThematicBreak,
  Table as MdTable,
  TableRow as MdTableRow,
  TableCell as MdTableCell,
  Image as MdImage,
  Link as MdLink,
  Text as MdText,
} from "mdast";

import {
  AlignmentType,
  BorderStyle,
  Document,
  HeadingLevel,
  ImageRun,
  LevelFormat,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TableLayoutType,
  TextRun,
  WidthType,
} from "docx";

const stripEmojisAndMarkdown = (text: string) => {
  // Remove emojis
  let result = text.replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF\uDC00-\uDFFF]|[\u2011-\u26FF]|[\uFE00-\uFE0F])/g, "");
  // Remove markdown formatting
  result = result.replace(/[*_#\[\]\(\)~`>+\-=|{}!]/g, "");
  return result;
}

const DOCX_PAGE_WIDTH_TWIP = 12240;
const DOCX_PAGE_HEIGHT_TWIP = 15840;
const DOCX_PAGE_MARGIN_TWIP = 1080;
const DOCX_CONTENT_WIDTH_TWIP = DOCX_PAGE_WIDTH_TWIP - 2 * DOCX_PAGE_MARGIN_TWIP;
const TABLE_BORDER = { color: "CBD5E1", size: 4, style: BorderStyle.SINGLE } as const;

function cellAlignment(alignment: "left" | "center" | "right" | null | undefined) {
  if (alignment === "center") return AlignmentType.CENTER;
  if (alignment === "right") return AlignmentType.RIGHT;
  return AlignmentType.LEFT;
}

/** Public API: turn Markdown into a .docx and trigger a download */
export async function downloadDocxFromMarkdown(markdown: string, citations: any[], filename = "document.docx") {
  const doc = await buildDocx(markdown, citations);
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  filename = stripEmojisAndMarkdown(filename);
  a.download = filename.endsWith(".docx") ? filename : `${filename}.docx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ---------------- core build ---------------- */

type Block = Paragraph | Table;
type Blockish = Block | Block[] | null;

export async function buildDocx(markdown: string, citations: any[] = []): Promise<Document> {
  const tree = parseMarkdown(stripEmojis(markdown));

  const children: Block[] = [];
  for (const node of tree.children) {
    pushBlocks(children, await renderBlock(node, { listLevel: 0, blockquoteIndentTwip: 0 }));
  }

  if (citations.length > 0) {
    children.push(new Paragraph({
      children: [new TextRun({ text: "Citations", bold: true, size: 20, color: "334155" })],
      border: { top: TABLE_BORDER },
      spacing: { before: 320, after: 120 },
      keepNext: true,
    }));

    for (const c of citations) {
      children.push(new Paragraph({
        children: [new TextRun({ text: `[${c.id}] ${stripEmojis(String(c.title))}`, size: 18, color: "475569" })],
        spacing: { after: 60, line: 276 },
      }));
    }
  }

  return new Document({
    numbering: {
      config: [
        {
          reference: "bullets",
          levels: [
            {
              level: 0,
              format: LevelFormat.BULLET,
              text: "\u2022",
              alignment: AlignmentType.LEFT,
              style: { paragraph: { indent: { left: 720, hanging: 360 } } },
            },
            {
              level: 1,
              format: LevelFormat.BULLET,
              text: "\u25E6",
              alignment: AlignmentType.LEFT,
              style: { paragraph: { indent: { left: 1080, hanging: 360 } } },
            },
          ],
        },
        {
          reference: "numbers",
          levels: [
            {
              level: 0,
              format: LevelFormat.DECIMAL,
              text: "%1.",
              alignment: AlignmentType.LEFT,
              style: { paragraph: { indent: { left: 720, hanging: 360 } } },
            },
            {
              level: 1,
              format: LevelFormat.DECIMAL,
              text: "%1.%2.",
              alignment: AlignmentType.LEFT,
              style: { paragraph: { indent: { left: 1080, hanging: 360 } } },
            },
          ],
        },
      ],
    },
    styles: {
      default: {
        document: {
          run: { font: "Arial", size: 22 },
          paragraph: { spacing: { after: 120, line: 324 } },
        },
      },
      paragraphStyles: [
        {
          id: "Heading1",
          name: "Heading 1",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: { font: "Arial", size: 36, bold: true, color: "1F2937" },
          paragraph: { spacing: { before: 240, after: 120 } },
        },
        {
          id: "Heading2",
          name: "Heading 2",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: { font: "Arial", size: 28, bold: true, color: "1F2937" },
          paragraph: { spacing: { before: 220, after: 100 } },
        },
        {
          id: "Heading3",
          name: "Heading 3",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: { font: "Arial", size: 24, bold: true, color: "1F2937" },
          paragraph: { spacing: { before: 180, after: 80 } },
        },
        {
          id: "Heading4",
          name: "Heading 4",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: { font: "Arial", size: 22, bold: true, color: "334155" },
          paragraph: { spacing: { before: 160, after: 80 } },
        },
        {
          id: "Heading5",
          name: "Heading 5",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: { font: "Arial", size: 22, bold: true, color: "334155" },
          paragraph: { spacing: { before: 160, after: 80 } },
        },
        {
          id: "Heading6",
          name: "Heading 6",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: { font: "Arial", size: 22, bold: true, color: "334155" },
          paragraph: { spacing: { before: 160, after: 80 } },
        },
      ],
    },
    sections: [{
      properties: {
        page: {
          size: { width: DOCX_PAGE_WIDTH_TWIP, height: DOCX_PAGE_HEIGHT_TWIP },
          margin: {
            top: DOCX_PAGE_MARGIN_TWIP,
            right: DOCX_PAGE_MARGIN_TWIP,
            bottom: DOCX_PAGE_MARGIN_TWIP,
            left: DOCX_PAGE_MARGIN_TWIP,
          },
        },
      },
      children,
    }],
  });
}

function parseMarkdown(md: string): Root {
  return unified().use(remarkParse).use(remarkGfm).parse(md) as Root;
}

function pushBlocks(target: Block[], val: Blockish) {
  if (!val) return;
  if (Array.isArray(val)) target.push(...val);
  else target.push(val);
}

type Ctx = {
  listLevel: number;
  blockquoteIndentTwip: number; // 1/20 pt; 360 = 0.25"
};

async function renderBlock(node: Content, ctx: Ctx): Promise<Blockish> {
  switch (node.type) {
    case "heading": {
      const h = node as MdHeading;
      const runs = await renderInline(h.children as PhrasingContent[]);
      const depth = Math.min(Math.max(h.depth, 1), 6);
      const headingLevels = [
        HeadingLevel.HEADING_1,
        HeadingLevel.HEADING_2,
        HeadingLevel.HEADING_3,
        HeadingLevel.HEADING_4,
        HeadingLevel.HEADING_5,
        HeadingLevel.HEADING_6,
      ] as const;

      return new Paragraph({
        heading: headingLevels[depth - 1],
        children: runs.length ? runs : [new TextRun("")],
        spacing: { before: depth === 1 ? 240 : 180, after: 120 },
        keepNext: true,
        indent: ctx.blockquoteIndentTwip ? { left: ctx.blockquoteIndentTwip } : undefined,
      });
    }

    case "paragraph": {
      const p = node as MdParagraph;
      const runs = await renderInline(p.children as PhrasingContent[]);
      return new Paragraph({
        children: runs.length ? runs : [new TextRun("")],
        spacing: { after: 120, line: 324 },
        indent: ctx.blockquoteIndentTwip ? { left: ctx.blockquoteIndentTwip } : undefined,
      });
    }

    case "list": {
      const l = node as MdList;
      const out: Block[] = [];
      for (const item of l.children as MdListItem[]) {
        const lines = await renderListItem(item, {
          ordered: !!l.ordered,
          level: ctx.listLevel,
          blockquoteIndentTwip: ctx.blockquoteIndentTwip,
        });
        out.push(...lines);
      }
      return out;
    }

    case "blockquote": {
      const bq = node as MdBlockquote;
      const nextCtx: Ctx = {
        listLevel: ctx.listLevel,
        blockquoteIndentTwip: ctx.blockquoteIndentTwip + 360, // +0.25"
      };
      const out: Block[] = [];
      for (const child of bq.children) {
        pushBlocks(out, await renderBlock(child, nextCtx));
      }
      return out;
    }

    case "code": {
      const c = node as MdCode;
      return new Paragraph({
        children: [new TextRun({ text: c.value, font: "Courier New", size: 18, color: "334155" })],
        border: {
          top: { color: "CBD5E1", size: 4, style: BorderStyle.SINGLE },
          bottom: { color: "CBD5E1", size: 4, style: BorderStyle.SINGLE },
          left: { color: "CBD5E1", size: 4, style: BorderStyle.SINGLE },
          right: { color: "CBD5E1", size: 4, style: BorderStyle.SINGLE },
        },
        shading: { fill: "F8FAFC" },
        spacing: { before: 120, after: 120, line: 276 },
        indent: ctx.blockquoteIndentTwip ? { left: ctx.blockquoteIndentTwip } : undefined,
      });
    }

    case "thematicBreak": {
      const _hr = node as MdThematicBreak;
      return new Paragraph({
        border: { bottom: { color: "E5E7EB", size: 12, style: BorderStyle.SINGLE } },
        spacing: { before: 120, after: 120 },
        indent: ctx.blockquoteIndentTwip ? { left: ctx.blockquoteIndentTwip } : undefined,
      });
    }

    case "table": {
      const t = node as MdTable;
      const rows: TableRow[] = [];
      const columnCount = t.children[0]?.children.length ?? 0;
      if (columnCount === 0) return null;
      const columnWidths = getTableColumnWidths(t, DOCX_CONTENT_WIDTH_TWIP);
      for (const [rowIndex, mdRow] of (t.children as MdTableRow[]).entries()) {
        const cells: TableCell[] = [];
        for (const [columnIndex, cell] of (mdRow.children as MdTableCell[]).entries()) {
          const isHeader = rowIndex === 0;
          const runs = await renderInline(
            (cell.children ?? []) as PhrasingContent[],
            isHeader ? { bold: true, size: 20, color: "334155" } : { size: 20, color: "334155" },
          );
          cells.push(new TableCell({
            width: { size: columnWidths[columnIndex], type: WidthType.DXA },
            verticalAlign: "top",
            margins: { top: 120, bottom: 120, left: 160, right: 160 },
            shading: isHeader ? { fill: "F1F5F9" } : rowIndex % 2 === 0 ? { fill: "F8FAFC" } : undefined,
            borders: {
              top: TABLE_BORDER,
              bottom: TABLE_BORDER,
              left: TABLE_BORDER,
              right: TABLE_BORDER,
            },
            children: [new Paragraph({
              alignment: cellAlignment(t.align?.[columnIndex]),
              children: runs.length ? runs : [new TextRun("")],
              spacing: { before: 0, after: 0, line: 276 },
            })],
          }));
        }
        rows.push(new TableRow({ children: cells, tableHeader: rowIndex === 0, cantSplit: true }));
      }
      return new Table({
        rows,
        width: { size: DOCX_CONTENT_WIDTH_TWIP, type: WidthType.DXA },
        columnWidths,
        layout: TableLayoutType.FIXED,
      });
    }

    case "image": {
      const img = node as MdImage;
      const imageRun = await imageRunFromUrl(img.url);
      return new Paragraph({
        children: imageRun ? [imageRun] : [new TextRun(`[image: ${img.url}]`)],
        indent: ctx.blockquoteIndentTwip ? { left: ctx.blockquoteIndentTwip } : undefined,
      });
    }

    default:
      return null; // ignore raw HTML/defs client-side
  }
}

/* --------------- lists --------------- */

type ListCtx = {
  ordered: boolean;
  level: number; // numbering level (0,1,…)
  blockquoteIndentTwip: number;
};

async function renderListItem(item: MdListItem, lctx: ListCtx): Promise<Block[]> {
  const out: Block[] = [];
  const [first, ...rest] = item.children;

  // main bullet/number line
  if (first && first.type === "paragraph") {
    const runs = await renderInline((first as MdParagraph).children as PhrasingContent[]);
    out.push(
      new Paragraph({
        children: runs.length ? runs : [new TextRun("")],
        numbering: { reference: lctx.ordered ? "numbers" : "bullets", level: Math.min(lctx.level, 1) },
        spacing: { after: 60, line: 276 },
        indent: lctx.blockquoteIndentTwip ? { left: lctx.blockquoteIndentTwip } : undefined,
      })
    );
  } else {
    out.push(
      new Paragraph({
        children: [new TextRun("")],
        numbering: { reference: lctx.ordered ? "numbers" : "bullets", level: Math.min(lctx.level, 1) },
        spacing: { after: 60, line: 276 },
        indent: lctx.blockquoteIndentTwip ? { left: lctx.blockquoteIndentTwip } : undefined,
      })
    );
  }

  // nested content
  for (const child of rest) {
    if (child.type === "list") {
      pushBlocks(
        out,
        await renderBlock(child as MdList, {
          listLevel: lctx.level + 1,
          blockquoteIndentTwip: lctx.blockquoteIndentTwip,
        })
      );
    } else {
      pushBlocks(
        out,
        await renderBlock(child as Content, {
          listLevel: lctx.level,
          blockquoteIndentTwip: lctx.blockquoteIndentTwip,
        })
      );
    }
  }

  return out;
}

/* --------------- inline --------------- */

async function renderInline(
  nodes: PhrasingContent[],
  flags: { bold?: boolean; italics?: boolean; strike?: boolean; code?: boolean; size?: number; color?: string } = {}
): Promise<Array<TextRun | ImageRun>> {
  const out: Array<TextRun | ImageRun> = [];

  for (const node of nodes) {
    switch (node.type) {
      case "text":
        out.push(
          new TextRun({
            text: (node as MdText).value,
            bold: flags.bold ? true : undefined,
            italics: flags.italics ? true : undefined,
            strike: flags.strike ? true : undefined,
            font: flags.code ? "Courier New" : undefined,
            size: flags.size,
            color: flags.color,
          })
        );
        break;

      case "emphasis":
        out.push(...(await renderInline(node.children as PhrasingContent[], { ...flags, italics: true })));
        break;

      case "strong":
        out.push(...(await renderInline(node.children as PhrasingContent[], { ...flags, bold: true })));
        break;

      case "delete":
        out.push(...(await renderInline(node.children as PhrasingContent[], { ...flags, strike: true })));
        break;

      case "inlineCode":
        out.push(new TextRun({
          text: node.value,
          font: "Courier New",
          bold: flags.bold ? true : undefined,
          size: flags.size,
          color: flags.color,
        }));
        break;

      case "break":
        out.push(new TextRun({ break: 1 }));
        break;

      case "link": {
        const ln = node as MdLink;
        const text = flattenText(ln.children as PhrasingContent[]) || ln.url;
        out.push(new TextRun({
          text: `${text} (${ln.url})`,
          bold: flags.bold ? true : undefined,
          size: flags.size,
          color: flags.color,
        })); // simple, compatible auto-link
        break;
      }

      case "image": {
        const img = node as MdImage;
        const run = await imageRunFromUrl(img.url);
        out.push(run ?? new TextRun(`[image: ${img.url}]`));
        break;
      }

      default:
        // ignore footnoteReference/html inlines client-side
        break;
    }
  }

  return out;
}

/* --------------- images --------------- */

async function imageRunFromUrl(url: string): Promise<ImageRun | null> {
  // try {
  //   const resp = await fetch(url, { mode: "cors" });
  //   if (!resp.ok) return null;
  //   const buf = await resp.arrayBuffer(); // <-- ArrayBuffer is accepted by docx
  //   return new ImageRun({ data: buf });
  // } catch {
  //   return null;
  // }
  return null;
}

/* --------------- utils --------------- */

function flattenText(nodes: PhrasingContent[]): string {
  let s = "";
  for (const n of nodes) {
    if (n.type === "text") s += (n as MdText).value;
    else if ("children" in n && Array.isArray((n as any).children)) {
      s += flattenText((n as any).children as PhrasingContent[]);
    }
  }
  return s;
}
